/**
 * TanStack Query hooks for Companies API
 *
 * Provides server state management with caching, background refetching,
 * and optimistic updates for companies/contacts.
 */

import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { companiesApi } from '../services/companiesApi';
import type {
  CompaniesListParams,
  CompaniesListResponse,
  Company,
  CompanyCategory,
  CompanyDefaultFee,
  CompanyEmployee,
  CompanyNote,
  CreateCompanyInput,
  CreateDefaultFeeInput,
  CreateEmployeeInput,
  CreateNoteInput,
  UpdateCompanyInput,
  UpdateDefaultFeeInput,
  UpdateEmployeeInput,
} from '../types';

/**
 * Query keys for cache management
 */
export const companyQueryKeys = {
  all: ['companies'] as const,
  categories: () => [...companyQueryKeys.all, 'categories'] as const,
  lists: () => [...companyQueryKeys.all, 'list'] as const,
  list: (params?: CompaniesListParams) => [...companyQueryKeys.lists(), params] as const,
  search: (query: string) => [...companyQueryKeys.all, 'search', query] as const,
  details: () => [...companyQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...companyQueryKeys.details(), id] as const,
  employees: (companyId: string) => [...companyQueryKeys.detail(companyId), 'employees'] as const,
  notes: (companyId: string) => [...companyQueryKeys.detail(companyId), 'notes'] as const,
  defaultFees: (companyId: string) =>
    [...companyQueryKeys.detail(companyId), 'defaultFees'] as const,
};

// =============================================================================
// Category Queries
// =============================================================================

/**
 * Fetch all company categories
 */
export function useCompanyCategories(
  options?: Omit<UseQueryOptions<CompanyCategory[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: companyQueryKeys.categories(),
    queryFn: () => companiesApi.listCategories(),
    staleTime: 10 * 60 * 1000, // Categories rarely change, cache for 10 minutes
    ...options,
  });
}

// =============================================================================
// Company Queries
// =============================================================================

/**
 * Fetch list of companies with optional filters
 */
export function useCompanies(
  params?: CompaniesListParams,
  options?: Omit<UseQueryOptions<CompaniesListResponse, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: companyQueryKeys.list(params),
    queryFn: () => companiesApi.list(params),
    ...options,
  });
}

/**
 * Fetch a single company by ID
 */
export function useCompany(
  id: string,
  options?: Omit<UseQueryOptions<Company, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: companyQueryKeys.detail(id),
    queryFn: () => companiesApi.getById(id),
    enabled: !!id,
    ...options,
  });
}

/**
 * Search companies by name
 */
export function useCompanySearch(
  query: string,
  limit = 10,
  options?: Omit<UseQueryOptions<Company[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: companyQueryKeys.search(query),
    queryFn: () => companiesApi.search(query, limit),
    enabled: query.length >= 2, // Only search with 2+ characters
    ...options,
  });
}

// =============================================================================
// Company Mutations
// =============================================================================

/**
 * Create a new company
 */
export function useCreateCompany(
  options?: Omit<UseMutationOptions<Company, Error, CreateCompanyInput>, 'mutationFn'>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCompanyInput) => companiesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companyQueryKeys.lists() });
    },
    ...options,
  });
}

/**
 * Update a company
 */
export function useUpdateCompany(
  options?: Omit<
    UseMutationOptions<Company, Error, { id: string; data: UpdateCompanyInput }>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCompanyInput }) =>
      companiesApi.update(id, data),
    onSuccess: (updatedCompany) => {
      queryClient.setQueryData(companyQueryKeys.detail(updatedCompany.id), updatedCompany);
      queryClient.invalidateQueries({ queryKey: companyQueryKeys.lists() });
    },
    ...options,
  });
}

/**
 * Delete a company
 */
export function useDeleteCompany(
  options?: Omit<UseMutationOptions<void, Error, string>, 'mutationFn'>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => companiesApi.delete(id),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: companyQueryKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: companyQueryKeys.lists() });
    },
    ...options,
  });
}

// =============================================================================
// Employee Queries & Mutations
// =============================================================================

/**
 * Fetch employees for a company
 */
export function useCompanyEmployees(
  companyId: string,
  options?: Omit<UseQueryOptions<CompanyEmployee[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: companyQueryKeys.employees(companyId),
    queryFn: () => companiesApi.listEmployees(companyId),
    enabled: !!companyId,
    ...options,
  });
}

/**
 * Create an employee
 */
export function useCreateEmployee(
  options?: Omit<
    UseMutationOptions<CompanyEmployee, Error, { companyId: string; data: CreateEmployeeInput }>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ companyId, data }: { companyId: string; data: CreateEmployeeInput }) =>
      companiesApi.createEmployee(companyId, data),
    onSuccess: (_data, { companyId }) => {
      queryClient.invalidateQueries({ queryKey: companyQueryKeys.employees(companyId) });
      queryClient.invalidateQueries({ queryKey: companyQueryKeys.detail(companyId) });
    },
    ...options,
  });
}

/**
 * Update an employee
 */
export function useUpdateEmployee(
  options?: Omit<
    UseMutationOptions<
      CompanyEmployee,
      Error,
      { companyId: string; employeeId: string; data: UpdateEmployeeInput }
    >,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      employeeId,
      data,
    }: {
      companyId: string;
      employeeId: string;
      data: UpdateEmployeeInput;
    }) => companiesApi.updateEmployee(companyId, employeeId, data),
    onSuccess: (_data, { companyId }) => {
      queryClient.invalidateQueries({ queryKey: companyQueryKeys.employees(companyId) });
      queryClient.invalidateQueries({ queryKey: companyQueryKeys.detail(companyId) });
    },
    ...options,
  });
}

/**
 * Delete an employee
 */
export function useDeleteEmployee(
  options?: Omit<
    UseMutationOptions<void, Error, { companyId: string; employeeId: string }>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ companyId, employeeId }: { companyId: string; employeeId: string }) =>
      companiesApi.deleteEmployee(companyId, employeeId),
    onSuccess: (_data, { companyId }) => {
      queryClient.invalidateQueries({ queryKey: companyQueryKeys.employees(companyId) });
      queryClient.invalidateQueries({ queryKey: companyQueryKeys.detail(companyId) });
    },
    ...options,
  });
}

// =============================================================================
// Notes Queries & Mutations
// =============================================================================

/**
 * Fetch notes for a company
 */
export function useCompanyNotes(
  companyId: string,
  options?: Omit<UseQueryOptions<CompanyNote[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: companyQueryKeys.notes(companyId),
    queryFn: () => companiesApi.listNotes(companyId),
    enabled: !!companyId,
    ...options,
  });
}

/**
 * Create a note
 */
export function useCreateNote(
  options?: Omit<
    UseMutationOptions<CompanyNote, Error, { companyId: string; data: CreateNoteInput }>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ companyId, data }: { companyId: string; data: CreateNoteInput }) =>
      companiesApi.createNote(companyId, data),
    onSuccess: (_data, { companyId }) => {
      queryClient.invalidateQueries({ queryKey: companyQueryKeys.notes(companyId) });
      queryClient.invalidateQueries({ queryKey: companyQueryKeys.detail(companyId) });
    },
    ...options,
  });
}

/**
 * Delete a note
 */
export function useDeleteNote(
  options?: Omit<
    UseMutationOptions<void, Error, { companyId: string; noteId: string }>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ companyId, noteId }: { companyId: string; noteId: string }) =>
      companiesApi.deleteNote(companyId, noteId),
    onSuccess: (_data, { companyId }) => {
      queryClient.invalidateQueries({ queryKey: companyQueryKeys.notes(companyId) });
      queryClient.invalidateQueries({ queryKey: companyQueryKeys.detail(companyId) });
    },
    ...options,
  });
}

// =============================================================================
// Default Fees Queries & Mutations
// =============================================================================

/**
 * Fetch default fees for a company
 */
export function useCompanyDefaultFees(
  companyId: string,
  options?: Omit<UseQueryOptions<CompanyDefaultFee[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: companyQueryKeys.defaultFees(companyId),
    queryFn: () => companiesApi.listDefaultFees(companyId),
    enabled: !!companyId,
    ...options,
  });
}

/**
 * Create a default fee
 */
export function useCreateDefaultFee(
  options?: Omit<
    UseMutationOptions<
      CompanyDefaultFee,
      Error,
      { companyId: string; data: CreateDefaultFeeInput }
    >,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ companyId, data }: { companyId: string; data: CreateDefaultFeeInput }) =>
      companiesApi.createDefaultFee(companyId, data),
    onSuccess: (_data, { companyId }) => {
      queryClient.invalidateQueries({ queryKey: companyQueryKeys.defaultFees(companyId) });
      queryClient.invalidateQueries({ queryKey: companyQueryKeys.detail(companyId) });
    },
    ...options,
  });
}

/**
 * Update a default fee
 */
export function useUpdateDefaultFee(
  options?: Omit<
    UseMutationOptions<
      CompanyDefaultFee,
      Error,
      { companyId: string; feeId: string; data: UpdateDefaultFeeInput }
    >,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      feeId,
      data,
    }: {
      companyId: string;
      feeId: string;
      data: UpdateDefaultFeeInput;
    }) => companiesApi.updateDefaultFee(companyId, feeId, data),
    onSuccess: (_data, { companyId }) => {
      queryClient.invalidateQueries({ queryKey: companyQueryKeys.defaultFees(companyId) });
      queryClient.invalidateQueries({ queryKey: companyQueryKeys.detail(companyId) });
    },
    ...options,
  });
}

/**
 * Delete a default fee
 */
export function useDeleteDefaultFee(
  options?: Omit<
    UseMutationOptions<void, Error, { companyId: string; feeId: string }>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ companyId, feeId }: { companyId: string; feeId: string }) =>
      companiesApi.deleteDefaultFee(companyId, feeId),
    onSuccess: (_data, { companyId }) => {
      queryClient.invalidateQueries({ queryKey: companyQueryKeys.defaultFees(companyId) });
      queryClient.invalidateQueries({ queryKey: companyQueryKeys.detail(companyId) });
    },
    ...options,
  });
}
